import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { UtilService } from "../../services";
import { isNonProd } from "../../utils/envConfig";
import "./LanguageSelect.css";
import "./CountryOverrideSelect.css";

function getApiErrorMessage(error) {
  if (typeof error === "string") {
    return error;
  }
  if (error?.category === "COUNTRY" && error?.code === "1000") {
    return "Unknown country.";
  }
  if (error?.message) {
    return error.message;
  }
  return "Failed to update country override. Please try again.";
}

export function CountryOverrideSelect({ onRegionChange }) {
  const [countries, setCountries] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [listVisible, setListVisible] = useState(false);
  const [search, setSearch] = useState("");

  const hideList = useCallback(() => {
    setListVisible(false);
    setSearch("");
  }, []);

  useEffect(() => {
    if (!listVisible) {
      return undefined;
    }

    const handleDocumentClick = () => hideList();
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [listVisible, hideList]);

  useEffect(() => {
    if (!isNonProd()) {
      return;
    }

    let cancelled = false;

    Promise.all([UtilService.getCountries(), UtilService.getCountryOverride()])
      .then(([countryList, override]) => {
        if (cancelled) {
          return;
        }
        setCountries(countryList || []);
        setSelectedCode(override?.countryCode || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return countries;
    }
    return countries.filter((country) => {
      const name = (country.name || "").toLowerCase();
      const code = (country.countryCode || "").toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [countries, search]);

  const applyOverride = useCallback(
    async (action) => {
      setUpdating(true);
      setError(null);
      try {
        const override = await action();
        setSelectedCode(override?.countryCode || null);
        await onRegionChange();
      } catch (err) {
        setError(getApiErrorMessage(err));
      } finally {
        setUpdating(false);
      }
    },
    [onRegionChange]
  );

  const handleSelect = useCallback(
    (country) => {
      hideList();
      if (!country?.countryCode || country.countryCode === selectedCode) {
        return;
      }
      applyOverride(() => UtilService.setCountryOverride(country.countryCode));
    },
    [applyOverride, hideList, selectedCode]
  );

  const showList = useCallback((event) => {
    event.stopPropagation();
    if (loading || updating) {
      return;
    }
    setListVisible(true);
  }, [loading, updating]);

  if (!isNonProd()) {
    return null;
  }

  const displayLabel = selectedCode || "Country";

  return (
    <div className="tdsCountryOverride">
      <div
        className={
          "dropdown-container tdsCountryOverride__dropdown" +
          (listVisible ? " show" : "")
        }
      >
        <div
          className={
            "dropdown-display" + (listVisible ? " clicked" : "")
          }
          onClick={showList}
        >
          <span>
            {displayLabel} <i className="fas fa-caret-down"></i>
          </span>
        </div>
        <div className="dropdown-list">
          <div className="tdsCountryOverride__panel">
            <div className="tdsCountryOverride__search">
              <input
                type="text"
                value={search}
                placeholder="Search country"
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {filteredCountries.map((country) => (
              <div
                key={country.countryCode}
                className="tdsCountryOverride__option"
                onClick={() => handleSelect(country)}
              >
                <span>
                  {country.name || country.countryCode} ({country.countryCode})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {error && <span className="tdsCountryOverride__error">{error}</span>}
    </div>
  );
}

CountryOverrideSelect.propTypes = {
  onRegionChange: PropTypes.func.isRequired,
};
